/**
 * @file target-management.tsx
 * @module app/modules/sales-management/sales-manager/target-management/target-management
 * @description Implements the target management interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from 'react';
import { Button, Card, Col, Row, FormLabel, Tooltip, OverlayTrigger } from 'react-bootstrap';
import Loader from '../../../../../Global/loader';
import AddEditTargetModal from '../../../../modals/add-edit-target-modal';
import APICallService from '../../../../../api/apiCallService';
import { assistAdmin, targetManagement } from '../../../../../api/apiEndPoints';
import { targetManagementJson } from '../../../../../api/apiJSON/targetManagement';
import { TargetManagement as TargetManagementConst, View, PAGE_LIMIT } from '../../../../../utils/constants';
import { getKey, setKey } from '../../../../../Global/history';
import { listTargetManagement } from '../../../../../utils/storeString';
import { KTSVG } from '../../../../../_admin/helpers';
import { CustomSelect } from '../../../../custom/Select/CustomSelect';
import { REPRESENTATIVE_TARGET_PERIOD } from '../../../../../utils/staticJSON';
import Pagination from '../../../../../Global/pagination';
import Method from '../../../../../utils/methods';
import { useAuth } from '../../../auth';

const TargetManagement = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [targets, setTargets] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<any>(undefined);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState<any>(
    getKey(listTargetManagement.page) || 1
  );
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listTargetManagement.limit) || PAGE_LIMIT
  );
  const [search, setSearch] = useState<any>(
    getKey(listTargetManagement.search) || ""
  );
  const [period, setPeriod] = useState<any>(
    getKey(listTargetManagement.period) || undefined
  );

  useEffect(() => {
    fetchTargetsList(page, pageLimit, search, period);
  }, []);

  const fetchTargetsList = async (pageNo = 1, limit = 10, searchTerm = "", periodVal = undefined) => {
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "createdAt",
      sortOrder: -1,
      needCount: true,
      "role[0]": 2,
      "role[1]": 7,
      searchTerm: searchTerm,
      manager: currentUser?._id,
    };
    if (periodVal !== null && periodVal !== undefined && periodVal !== "" && periodVal !== "null") {
      params.targetType = periodVal;
    }
    const apiService = new APICallService(
      assistAdmin.list,
      params, 
      "",
      "",
      "",
      "",
      TargetManagementConst,
    );

    const responseFromAPI = await apiService.callAPI();
    
    if (responseFromAPI && responseFromAPI.records) {
      setTargets(responseFromAPI.records || []);
      setTotalRecords(responseFromAPI.total || 0);
    } else {
      setTargets([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setKey(listTargetManagement.page, val);
    await fetchTargetsList(val, pageLimit, search, period);
  };

  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setKey(listTargetManagement.page, val + 1);
    await fetchTargetsList(val + 1, pageLimit, search, period);
  };

  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    setKey(listTargetManagement.page, val - 1);
    await fetchTargetsList(val - 1, pageLimit, search, period);
  };

  const handlePageLimit = async (event: any) => {
    const limit = parseInt(event.target.value);
    setPage(1);
    setPageLimit(limit);
    setKey(listTargetManagement.page, 1);
    setKey(listTargetManagement.limit, limit);
    await fetchTargetsList(1, limit, search, period);
  };

  const handleSearch = async (value: any) => {
    setSearch(value);
    setPage(1);
    setKey(listTargetManagement.search, value, false);
    setKey(listTargetManagement.page, 1);
    await fetchTargetsList(1, pageLimit, value, period);
  };

  const handlePeriodFilter = async (value: any) => {
    setPeriod(value);
    setPage(1);
    setKey(listTargetManagement.period, value);
    setKey(listTargetManagement.page, 1);
    await fetchTargetsList(1, pageLimit, search, value);
  };

  const handleAssignClick = (target?: any) => {
    setSelectedTarget(target || undefined);
    setShowModal(true);
  };

  const handleSaveTarget = () => {
    fetchTargetsList(page, pageLimit, search, period);
  };

  const renderTooltip = (props: any, target: any) => (
    <Tooltip
      id={`notes-tooltip-${target.id || target._id}`}
      className="tooltip tooltip-notes"
      {...props}
    >
      <p className="text-white mb-0 fs-14 fw-500">
        {target.currentSalesTarget.notes}
      </p>
    </Tooltip>
  );

  return (
    <div className="p-5">
      <Row className="align-items-center mb-7 g-5">
        <Col xs>
          <h1 className="fs-22 fw-bolder">Target Management</h1>
        </Col>
      </Row>

      <Card className="bg-light border my-7">
        <Card.Body className="px-7">
          <Row className="align-items-center gy-5">
            <Col sm={12} md={6} lg={4}>
              <FormLabel className="fs-16 fw-500">
                Search by Rep Name
              </FormLabel>
              <div className="d-flex align-items-center position-relative">
                <KTSVG
                  path="/media/icons/duotune/general/gen021.svg"
                  className="svg-icon-3 position-absolute ms-3"
                />
                <input
                  type="text"
                  className="form-control form-control-custom borde-r8px bg-white ps-11"
                  placeholder="Search"
                  value={search}
                  onChange={(event: any) => {
                    handleSearch(event.target.value);
                  }}
                />
              </div>
            </Col>
            <Col sm={12} md={6} lg={4}>
              <FormLabel className="fs-16 fw-500">
                Filter by period
              </FormLabel>
              <div>
                <CustomSelect
                  backgroundColor="#ffff"
                  value={REPRESENTATIVE_TARGET_PERIOD.find(
                    (option) => option?.value == period
                  )}
                  options={REPRESENTATIVE_TARGET_PERIOD.filter(opt => opt.value !== -1)}
                  onChange={(event: any) =>
                    handlePeriodFilter(event ? event.value : undefined)
                  }
                  isSearchable={false}
                  isMulti={false}
                  isClearable={true}
                  placeholder="Select"
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!loading ? (
        <>
          <Card className="border border-r10px">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                  <thead>
                    <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                      <th className="min-w-200px">Rep Name</th>
                      <th className="min-w-150px">Target Amount</th>
                      <th className="min-w-150px">Achieved</th>
                      <th className="min-w-150px">Remaining</th>
                      <th className="min-w-100px text-center">Period</th>
                      <th className="min-w-150px">Notes</th>
                      <th className="min-w-180px text-end"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {targets.length > 0 ? (
                      targets.map((target) => (
                        <tr key={target.id || target._id}>
                          <td>
                            <div className="d-flex flex-column">
                              <span className="text-dark fw-bolder fs-15">{target.name}</span>
                              <span className="text-gray-500 fs-13">{target.email}</span>
                            </div>
                          </td>
                          <td className="fs-15 fw-500">
                            <span className="text-dark fw-bold">{target?.currentSalesTarget?.target ? 'Tsh ' + Method.getGeneralizedAmount(target?.currentSalesTarget?.target) : "NA"}</span>
                          </td>
                          <td className="fs-15 fw-500">
                            <span className="text-dark fw-bold">{target?.currentSalesTarget?.achieved === 0 ? "NA" : 'Tsh ' + Method.getGeneralizedAmount(target?.currentSalesTarget?.achieved) || "NA"}</span>
                          </td>
                          <td className="fs-15 fw-500">
                            <span className="text-dark fw-bold">
                              {target?.currentSalesTarget?.target > 0 ? 'Tsh ' + Method.getGeneralizedAmount(Math.max(
                                0,
                                (target?.currentSalesTarget?.target ?? 0) -
                                (target?.currentSalesTarget?.achieved ?? 0)
                              )) : "NA"}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge rounded-pill bg-light text-dark py-3 px-5 fs-14 fw-500">
                              {Method.getSalesTargetTypesLabel(target?.currentSalesTarget?.type)}
                            </span>
                          </td>
                          <td className="fs-15 fw-500">
                            {target?.currentSalesTarget?.notes ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={(props) => renderTooltip(props, target)}
                              >
                                <span
                                  className="text-dark fw-bold"
                                  style={{
                                    maxWidth: "320px",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {target.currentSalesTarget.notes}
                                </span>
                              </OverlayTrigger>
                            ) : (
                              <span className="text-dark fw-bold">NA</span>
                            )}
                          </td>
                          <td className="text-end">
                            <Button
                              variant="primary"
                              className="uw-btn uw-btn-primary fs-14 fw-bold px-8 py-3 min-w-150px"
                              onClick={() => handleAssignClick(target)}
                            >
                              {target?.currentSalesTarget?.reference ? "Edit Targets" : "Assign Targets"}
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="fs-15 fw-600 text-center">
                          No Data Found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
          {targets.length > 0 && (
            <Pagination
              totalRecords={totalRecords}
              currentPage={page}
              handleCurrentPage={handleCurrentPage}
              handleNextPage={handleNextPage}
              handlePreviousPage={handlePreviousPage}
              handlePageLimit={handlePageLimit}
              pageLimit={pageLimit}
            />
          )}
        </>
      ) : (
        <div className="border border-r10px mb-6">
          <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
            <Loader loading={loading} />
          </div>
        </div>
      )}

      <AddEditTargetModal
        show={showModal}
        onHide={() => setShowModal(false)}
        targetData={selectedTarget}
        onSave={handleSaveTarget}
      />
    </div>
  );
};

export default TargetManagement;
